/**
 * Login Page
 * Handles Microsoft authentication
 * UnsOps branded design with gradient background
 */

import { FC } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Microsoft as MicrosoftIcon } from '@mui/icons-material';
import { useAuth } from '@auth/useAuth';
import { unsopsColors } from '@/providers/theme';
import { UnsOpsLogo } from '@/assets';

export const LoginPage: FC = () => {
  const { login, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: unsopsColors.gradient,
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: unsopsColors.gradient,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 80%, rgba(236,72,153,0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Card
        sx={{
          maxWidth: 420,
          width: '100%',
          mx: 2,
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <CardContent sx={{ p: 5, textAlign: 'center' }}>
          {/* Logo */}
          <Box sx={{ mb: 3 }}>
            <Box
              component="img"
              src={UnsOpsLogo}
              alt="UnsOps Logo"
              sx={{
                maxWidth: 200,
                height: 'auto',
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                background: unsopsColors.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              UnsOps Admin
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Portal de Administración
            </Typography>
          </Box>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, px: 2 }}>
            Accede con tu cuenta de Microsoft para administrar el ERP SaaS multi-tenant.
          </Typography>

          {/* Login Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<MicrosoftIcon />}
            onClick={login}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              background: unsopsColors.gradient,
              boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.39)',
              '&:hover': {
                background: unsopsColors.gradientDark,
                boxShadow: '0 6px 20px rgba(79, 70, 229, 0.5)',
              },
            }}
          >
            Iniciar sesión con Microsoft
          </Button>

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 4 }}
          >
            Acceso restringido a usuarios autorizados de UnsOps
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
